/* ============================================================
   ÉKO '77 — GALLERY LIGHTBOX

   A 1:1 port of the viewer in the inline script of the reference
   gallery page (https://eko77-redesign.pages.dev/gallery/), block
   "LIGHTBOX": open on click, previous / next, close, swipe on a
   phone, and the page behind held still while it is open.

   Reference behaviour reproduced exactly
     · click a photograph  -> opens it at that index
     · arrows / swipe left-right -> previous, next, wrapping round
     · Escape, the cross, a click on the scrim, or a swipe down -> close
     · Home / End -> first / last
     · Tab cycles the three controls and never leaves the dialog
     · the counter reads "n / total"
     · the frame fills the stage but is never blown past twice the
       original's own size (--x-lb-cw / --x-lb-ch)
     · the neighbours either side are preloaded
     · thresholds are the reference's: a swipe counts under 700ms,
       sideways past 45px, down past 90px, each 1.4x the other axis

   ------------------------------------------------------------------
   WHY THIS BINDS BY SELECTOR INSTEAD OF BY MARKUP

   The reference is one static page, so its script could hold a fixed
   list of `.gal-item` buttons. Here the photographs arrive from two
   SEPARATE gallery sections (the rooms grid and the food grid), each
   rendered by the theme editor, each able to be added, reordered or
   re-rendered after this script has run. So:

     · the click listener is DELEGATED on document — a grid that the
       theme editor drops in afterwards is live with no re-binding;
     · the running order is read from the DOM at the moment of the
       click, so it is always the order the visitor can actually see,
       across BOTH grids, exactly like the reference's single list;
     · a MutationObserver gives late arrivals the button semantics.

   A grid opts in in one of three ways, no code change here:
     · put data-x-lb-item on each photograph, or
     · give each photograph the class .x-gal-item, or
     · put data-x-lb-group on the grid — every <img> inside joins.
   Anything else: point "Photograph selector" in the theme editor at
   the class the grid actually uses. Nothing needs a developer.

   THE FULL-SIZE FILE. The reference hardcodes data-full / data-fw /
   data-fh per photograph. Here the thumbnail already carries a srcset
   of Shopify renditions, so the largest candidate is taken and its
   width is raised to the section's "Largest rendition" setting —
   Shopify never upscales past the original, so this asks for the best
   file that exists and no more. Both URL shapes this theme produces
   are handled: `?width=1600` (image_url) and `_1600x.jpg`
   (asset_img_url). data-x-lb-full still wins if a grid sets it.
   ============================================================ */

(function () {
  'use strict';

  if (customElements.get('x-lightbox')) return;

  var DEFAULT_SELECTOR = '[data-x-lb-item], .x-gal-item, .gal-item, [data-x-lb-group] img';
  var ITEM_CLASS = 'x-lb-item';
  var SWIPE_MS = 700;
  var SWIPE_X = 45;
  var SWIPE_Y = 90;
  var SWIPE_BIAS = 1.4;

  /* ---------------------------------------------------------- */
  /* helpers                                                     */
  /* ---------------------------------------------------------- */

  function imageOf(el) {
    if (!el) return null;
    return el.tagName === 'IMG' ? el : el.querySelector('img');
  }

  /* the widest candidate the browser was offered for the thumbnail */
  function widestOf(srcset) {
    if (!srcset) return '';
    var best = '';
    var bestW = -1;
    srcset.split(',').forEach(function (part) {
      var bits = part.trim().split(/\s+/);
      if (!bits[0]) return;
      var d = bits[1] || '';
      var w = 0;
      if (/^\d+w$/.test(d)) w = parseInt(d, 10);
      else if (/^[\d.]+x$/.test(d)) w = parseFloat(d) * 1000;
      if (w > bestW) {
        bestW = w;
        best = bits[0];
      }
    });
    return best;
  }

  /* ask the CDN for the biggest rendition worth having */
  function atWidth(url, want) {
    if (!url || !want) return url || '';
    var abs;
    try {
      abs = new URL(url, window.location.href);
    } catch (e) {
      return url;
    }
    if (!/cdn\.shopify\.com|\/cdn\/shop\//.test(abs.href)) return url;

    /* image_url -> ...jpg?width=600 */
    var current = parseInt(abs.searchParams.get('width'), 10);
    if (current) {
      if (current < want) abs.searchParams.set('width', String(want));
      return abs.href;
    }

    /* asset_img_url -> ..._600x.jpg  |  ..._600x750.jpg */
    var named = abs.pathname.replace(/_(\d+)x(\d+)?(?=\.[a-z0-9]+$)/i, function (m, w) {
      return parseInt(w, 10) < want ? '_' + want + 'x' : m;
    });
    if (named !== abs.pathname) {
      abs.pathname = named;
      return abs.href;
    }

    abs.searchParams.set('width', String(want));
    return abs.href;
  }

  function intAttr(el, name) {
    var v = parseInt(el.getAttribute(name), 10);
    return v > 0 ? v : 0;
  }

  /* ---------------------------------------------------------- */

  customElements.define(
    'x-lightbox',
    class XLightbox extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onItemKeydown = this.onItemKeydown.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onTouchStart = this.onTouchStart.bind(this);
        this.onTouchEnd = this.onTouchEnd.bind(this);
        this.onImgLoad = this.onImgLoad.bind(this);
        this.adopt = this.adopt.bind(this);
      }

      connectedCallback() {
        this.root = this.querySelector('[data-x-lb]');
        if (!this.root) return;

        this.stage = this.querySelector('[data-x-lb-stage]');
        this.img = this.querySelector('[data-x-lb-img]');
        this.counter = this.querySelector('[data-x-lb-count]');
        this.bClose = this.querySelector('[data-x-lb-close]');
        this.bPrev = this.querySelector('[data-x-lb-prev]');
        this.bNext = this.querySelector('[data-x-lb-next]');
        if (!this.stage || !this.img) return;

        this.selector = this.getAttribute('data-item-selector') || DEFAULT_SELECTOR;
        this.want = parseInt(this.getAttribute('data-max-width'), 10) || 1600;
        this.loop = this.getAttribute('data-loop') !== 'false';
        this.swipe = this.getAttribute('data-swipe') !== 'false';
        this.scrimCloses = this.getAttribute('data-close-on-backdrop') !== 'false';

        this.shots = [];
        this.cur = 0;
        this.open = false;
        this.savedY = 0;
        this.lastFocus = null;
        this.cache = {};

        document.addEventListener('click', this.onClick);
        document.addEventListener('keydown', this.onKeydown);
        document.addEventListener('keydown', this.onItemKeydown);
        window.addEventListener('resize', this.onResize, { passive: true });
        this.img.addEventListener('load', this.onImgLoad);

        if (this.swipe) {
          this.root.addEventListener('touchstart', this.onTouchStart, { passive: true });
          this.root.addEventListener('touchend', this.onTouchEnd, { passive: true });
        }

        this.bClose && this.bClose.addEventListener('click', this.hide.bind(this));
        this.bPrev && this.bPrev.addEventListener('click', this.go.bind(this, -1));
        this.bNext && this.bNext.addEventListener('click', this.go.bind(this, 1));

        /* give every photograph on the page button semantics, now and
           whenever the theme editor rewrites a section */
        this.adopt();
        this.watcher = new MutationObserver(
          function () {
            if (this.pending) return;
            this.pending = window.requestAnimationFrame(
              function () {
                this.pending = 0;
                this.adopt();
              }.bind(this)
            );
          }.bind(this)
        );
        this.watcher.observe(document.body, { childList: true, subtree: true });

        if (window.Shopify && window.Shopify.designMode) {
          this.onSelect = function (e) {
            if (e.target.contains(this) && this.collect().length) this.show(0, null);
          }.bind(this);
          this.onDeselect = function (e) {
            if (e.target.contains(this)) this.hide();
          }.bind(this);
          document.addEventListener('shopify:section:select', this.onSelect);
          document.addEventListener('shopify:section:deselect', this.onDeselect);
        }
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onClick);
        document.removeEventListener('keydown', this.onKeydown);
        document.removeEventListener('keydown', this.onItemKeydown);
        window.removeEventListener('resize', this.onResize);
        if (this.onSelect) document.removeEventListener('shopify:section:select', this.onSelect);
        if (this.onDeselect) document.removeEventListener('shopify:section:deselect', this.onDeselect);
        if (this.watcher) this.watcher.disconnect();
        if (this.open) this.unlock();
      }

      /* -------------------------------------------------------- */
      /* the running order, read fresh from the page               */
      /* -------------------------------------------------------- */

      collect() {
        var out = [];
        var found;
        try {
          found = document.querySelectorAll(this.selector);
        } catch (e) {
          return out; /* a mistyped selector must not take the page down */
        }
        Array.prototype.forEach.call(
          found,
          function (el) {
            if (this.contains(el)) return; /* never the viewer's own image */
            if (el.closest('[data-x-lb-skip]')) return;
            /* the home rail prints its stills twice and hides the copy —
               a screen reader meets them once, and so does the viewer */
            if (el.closest('[aria-hidden="true"]')) return;
            if (!imageOf(el)) return;
            out.push(el);
          }.bind(this)
        );
        return out;
      }

      adopt() {
        this.collect().forEach(function (el) {
          if (el.classList.contains(ITEM_CLASS)) return;
          el.classList.add(ITEM_CLASS);
          /* a real <button> already has all of this */
          if (el.tagName === 'BUTTON' || el.tagName === 'A') return;
          if (!el.hasAttribute('role')) el.setAttribute('role', 'button');
          if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
        });
      }

      indexOfTarget(el) {
        this.shots = this.collect().map(function (node) {
          var img = imageOf(node);
          return {
            el: node,
            img: img,
            full:
              node.getAttribute('data-x-lb-full') ||
              atWidth(widestOf(img.getAttribute('srcset')) || img.currentSrc || img.src, this.want),
            w: intAttr(node, 'data-x-lb-w') || img.naturalWidth || intAttr(img, 'width') || 1600,
            h: intAttr(node, 'data-x-lb-h') || img.naturalHeight || intAttr(img, 'height') || 1600,
            alt: node.getAttribute('data-x-lb-alt') || img.getAttribute('alt') || ''
          };
        }, this);
        for (var i = 0; i < this.shots.length; i++) {
          if (this.shots[i].el === el) return i;
        }
        return -1;
      }

      /* -------------------------------------------------------- */
      /* the page behind                                           */
      /* -------------------------------------------------------- */

      lock() {
        this.savedY = window.scrollY || document.documentElement.scrollTop;
        var bar = window.innerWidth - document.documentElement.clientWidth;
        var s = document.body.style;
        s.position = 'fixed';
        s.top = -this.savedY + 'px';
        s.left = '0';
        s.right = '0';
        s.width = '100%';
        s.overflow = 'hidden';
        if (bar > 0) s.paddingRight = bar + 'px';
      }

      unlock() {
        var s = document.body.style;
        s.position = '';
        s.top = '';
        s.left = '';
        s.right = '';
        s.width = '';
        s.overflow = '';
        s.paddingRight = '';
        /* the page is set to scroll smoothly — going back must not be a journey */
        var root = document.documentElement;
        var prev = root.style.scrollBehavior;
        root.style.scrollBehavior = 'auto';
        window.scrollTo(0, this.savedY);
        root.style.scrollBehavior = prev;
      }

      /* -------------------------------------------------------- */
      /* the frame                                                 */
      /* -------------------------------------------------------- */

      preload(i) {
        if (!this.shots.length) return;
        if (this.loop) i = (i + this.shots.length) % this.shots.length;
        if (i < 0 || i >= this.shots.length || this.cache[i]) return;
        var im = new Image();
        im.src = this.shots[i].full;
        this.cache[i] = im;
      }

      /* size the frame against the room it actually has: fill the stage,
         but never blow a small original up past twice its own size */
      fit() {
        var s = this.shots[this.cur];
        if (!s) return;
        var cs = getComputedStyle(this.stage);
        var bw = parseFloat(getComputedStyle(this.img).borderLeftWidth) * 2 || 0;
        var availW =
          this.stage.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight) - bw;
        var availH =
          this.stage.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom) - bw;
        if (!(availW > 0) || !(availH > 0)) return;
        var scale = Math.min(availW / s.w, availH / s.h, 2);
        this.img.style.width = Math.round(s.w * scale + bw) + 'px';
        this.img.style.height = Math.round(s.h * scale + bw) + 'px';
      }

      paint() {
        var s = this.shots[this.cur];
        if (!s) return;
        this.img.classList.remove('is-on');
        this.img.style.setProperty('--x-lb-cw', s.w * 2 + 'px');
        this.img.style.setProperty('--x-lb-ch', s.h * 2 + 'px');
        this.fit();
        this.img.alt = s.alt;
        this.img.src = s.full;
        if (this.img.complete) this.onImgLoad();
        if (this.counter) {
          this.counter.textContent = this.cur + 1 + ' / ' + this.shots.length;
        }
        this.preload(this.cur + 1);
        this.preload(this.cur - 1);
      }

      /* the thumbnail only told us its own size; once the full file is
         here, re-measure against the real original so the 2x ceiling
         means what it means in the reference */
      onImgLoad() {
        var s = this.shots[this.cur];
        if (s && this.img.naturalWidth && this.img.naturalHeight) {
          if (this.img.naturalWidth !== s.w || this.img.naturalHeight !== s.h) {
            s.w = this.img.naturalWidth;
            s.h = this.img.naturalHeight;
            this.img.style.setProperty('--x-lb-cw', s.w * 2 + 'px');
            this.img.style.setProperty('--x-lb-ch', s.h * 2 + 'px');
            this.fit();
          }
        }
        this.img.classList.add('is-on');
      }

      /* -------------------------------------------------------- */
      /* open / close / move                                       */
      /* -------------------------------------------------------- */

      show(i, origin) {
        if (!this.shots.length) this.indexOfTarget(null);
        if (!this.shots.length) return;
        this.cur = Math.max(0, Math.min(i, this.shots.length - 1));
        this.lastFocus = origin || this.shots[this.cur].el;
        this.open = true;
        this.root.hidden = false;
        this.root.setAttribute('data-solo', this.shots.length < 2 ? 'true' : 'false');
        this.lock();
        this.paint();
        if (this.bClose) this.bClose.focus({ preventScroll: true });
      }

      hide() {
        if (!this.open) return;
        this.open = false;
        this.root.hidden = true;
        this.img.removeAttribute('src');
        this.unlock();
        if (this.lastFocus && document.contains(this.lastFocus)) {
          this.lastFocus.focus({ preventScroll: true });
        }
      }

      go(step) {
        if (!this.open || this.shots.length < 2) return;
        var next = this.cur + step;
        if (this.loop) {
          next = (next + this.shots.length) % this.shots.length;
        } else {
          if (next < 0 || next >= this.shots.length) return;
        }
        this.cur = next;
        this.paint();
      }

      /* -------------------------------------------------------- */
      /* input                                                     */
      /* -------------------------------------------------------- */

      onClick(e) {
        /* a click on the scrim, the empty stage or the counter row closes */
        if (this.open && this.scrimCloses) {
          if (e.target === this.root || e.target === this.stage || e.target.closest('.x-lb__bar')) {
            this.hide();
            return;
          }
        }
        if (this.root.contains(e.target)) return;

        var hit;
        try {
          hit = e.target.closest(this.selector);
        } catch (err) {
          return;
        }
        if (!hit || this.contains(hit)) return;
        /* a linked still keeps its link — unless it asked for the viewer */
        var link = hit.tagName === 'A' ? hit : hit.querySelector('a[href]');
        if (link && !hit.hasAttribute('data-x-lb-item')) return;

        var i = this.indexOfTarget(hit);
        if (i < 0) return;
        e.preventDefault();
        this.show(i, hit);
      }

      /* the reference's photographs are <button>s; a <figure> we adopted
         needs Enter and Space wiring by hand */
      onItemKeydown(e) {
        if (this.open) return;
        if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
        var el = document.activeElement;
        if (!el || el.tagName === 'BUTTON' || el.tagName === 'A') return;
        if (!el.classList || !el.classList.contains(ITEM_CLASS)) return;
        var i = this.indexOfTarget(el);
        if (i < 0) return;
        e.preventDefault();
        this.show(i, el);
      }

      onKeydown(e) {
        if (!this.open) return;
        if (e.key === 'Escape') {
          e.preventDefault();
          this.hide();
          return;
        }
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.go(-1);
          return;
        }
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.go(1);
          return;
        }
        if (e.key === 'Home') {
          e.preventDefault();
          this.cur = 0;
          this.paint();
          return;
        }
        if (e.key === 'End') {
          e.preventDefault();
          this.cur = this.shots.length - 1;
          this.paint();
          return;
        }
        if (e.key === 'Tab') {
          /* focus stays inside the dialog */
          var ring = [this.bClose, this.bPrev, this.bNext].filter(function (b) {
            return b && b.offsetParent !== null;
          });
          if (!ring.length) return;
          var at = ring.indexOf(document.activeElement);
          e.preventDefault();
          ring[
            e.shiftKey ? (at <= 0 ? ring.length - 1 : at - 1) : (at + 1) % ring.length
          ].focus();
        }
      }

      onResize() {
        if (this.open) this.fit();
      }

      onTouchStart(e) {
        var t = e.changedTouches[0];
        this.sx = t.clientX;
        this.sy = t.clientY;
        this.st = Date.now();
      }

      onTouchEnd(e) {
        if (!this.open) return;
        var t = e.changedTouches[0];
        var dx = t.clientX - this.sx;
        var dy = t.clientY - this.sy;
        if (Date.now() - this.st > SWIPE_MS) return;
        if (Math.abs(dx) > SWIPE_X && Math.abs(dx) > Math.abs(dy) * SWIPE_BIAS) {
          this.go(dx < 0 ? 1 : -1);
        } else if (Math.abs(dy) > SWIPE_Y && Math.abs(dy) > Math.abs(dx) * SWIPE_BIAS) {
          this.hide();
        }
      }
    }
  );
})();
